import { useEffect, useState } from 'react';
import { useBrand } from '../../brand/BrandProvider';
import { useSellablePackages, useSellablePrices } from '../../api/catalog';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Building2, Check, CreditCard, Loader2, Server } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Stepper } from '../../components/Stepper';
import { PageHeader } from '../../components/PageHeader';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';
import { ApiError, apiPost } from '../../api/client';
import { ByoDbFields, EMPTY_BYO_DB, byoDbError, toByoDbCreds, type ByoDbValue } from '../../components/ByoDbFields';
import { startCheckout } from '../../api/billing';
import { PolicyConsentCheckbox } from '../../components/PolicyConsent';

const STEPS = ['Business details', 'Choose a plan', 'Review & pay'];

/**
 * Add a new business — §5.10
 * An existing owner adds another shop: create the business (owner = the signed-in
 * user — enforced server-side), then pay the first invoice via the hosted
 * SSLCommerz page. If the gateway hand-off fails the invoice stays open on
 * /billing.
 *
 * Step 1 mirrors /signup's "Subdomain & DB" step: the same subdomain field and the
 * same shared/isolated database choice, gated by the host's own reseller policy.
 * Deliberately NOT at parity: signup's free-trial checkbox (trial length comes from
 * the plan — the API rejects a client-supplied one) and self-hosted BYO-DB (no way
 * to collect the customer's database credentials here).
 */
export function NewBusiness() {
  const navigate = useNavigate();
  const brand = useBrand();
  const [step, setStep] = useState(0);

  // step 1
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');

  // Database mode — same options /signup offers on this host, read from the same
  // allowed_db_modes, so the two surfaces can never drift apart. Platform hosts and
  // Ogrogoti now sell ISOLATED only, so on those this collapses to a single mode and
  // the picker below states it rather than offering a one-option choice.
  const [db, setDb] = useState<'shared' | 'managed' | 'self_hosted'>('shared');
  const canShared = brand.allowedDbModes.includes('shared');
  const canManaged = brand.allowedDbModes.includes('managed');
  const canSelfHosted = brand.allowedDbModes.includes('self_hosted');
  const offeredModes = (['shared', 'managed', 'self_hosted'] as const).filter((m) => brand.allowedDbModes.includes(m));
  useEffect(() => {
    // Never leave the form holding a mode this host does not sell. Written against the
    // offered list rather than as a pair of hand-written swaps, which silently did nothing
    // once a third mode existed — a host selling only self_hosted would have kept 'shared'
    // selected and had the create refused by the server.
    if (offeredModes.length > 0 && !offeredModes.includes(db)) setDb(offeredModes[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShared, canManaged, canSelfHosted, db]);

  // BYO-DB connection details. The "Test connection" button is available here because this
  // page is behind a login, unlike public signup.
  const [byod, setByod] = useState<ByoDbValue>(EMPTY_BYO_DB);

  // The real serving host for an isolated business is <slug>.pos.<apex> (wildcard DNS
  // + wildcard TLS). Shared businesses are all served on the reseller's own POS host,
  // so we must NOT show them a per-business address that will never resolve to them.
  const slugPreview = subdomain.trim() || 'your-shop';
  const posHostPreview = `${slugPreview}.pos.${brand.domain}`;

  // step 2 — plan catalog from the API, scoped SERVER-SIDE to the catalog this
  // host sells (a reseller's owner buys the reseller's plans, never ITCity's).
  const { data: packages = [], isLoading: plansLoading } = useSellablePackages();
  const { data: prices = [] } = useSellablePrices();
  const sellable = packages.filter((p) => p.status === 'published' && p.visibility === 'public');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [planId, setPlanId] = useState('');
  useEffect(() => {
    const first = sellable[0];
    if (!planId && first) setPlanId(first.id);
  }, [sellable, planId]);

  const priceFor = (pkgId: string, iv: 'month' | 'year') =>
    prices.find((pr) => pr.packageId === pkgId && pr.interval === iv && pr.status === 'active');
  const selectedPkg = sellable.find((p) => p.id === planId);
  const selectedPrice = priceFor(planId, interval) ?? priceFor(planId, 'month');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subdomainValid = subdomain === '' || /^[a-z0-9-]{3,}$/.test(subdomain);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const stepValid =
    step === 0 ? name.trim().length >= 2 && subdomainValid : step === 1 ? !!selectedPrice : acceptedPolicies;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      // The database step keeps its Continue button ENABLED and says what is missing, rather
      // than greying out and leaving the customer to work out which field it wants.
      if (step === 0 && db === 'self_hosted') {
        const invalid = byoDbError(byod);
        if (invalid) {
          setError(invalid);
          return;
        }
      }
      setError(null);
      if (stepValid) setStep((s) => s + 1);
      return;
    }
    if (!selectedPrice) return;
    setSubmitting(true);
    setError(null);
    let createdId: string | null = null;
    try {
      const created = await apiPost<{ id: string; name: string; status: string }>('/businesses', {
        name: name.trim(),
        // The typed subdomain used to be posted as `subtitle`, so it landed in the wrong
        // column and every self-serve business kept slug NULL. It is the slug now.
        subdomain: subdomain.trim() || undefined,
        dbMode: db,
        selfHostedCreds: db === 'self_hosted' ? toByoDbCreds(byod) : undefined,
        priceId: selectedPrice.id,
        paymentMode: 'online',
      });
      createdId = created.id;
    } catch (e) {
      // Show the server's own reason. A blind catch here reported a generic failure for
      // two weeks while the API was returning a precise 400 the whole time (the
      // trialDays regression) — the message is the only thing that says WHAT went wrong.
      setError(e instanceof ApiError && e.message ? e.message : 'Could not create the business. Please try again.');
      setSubmitting(false);
      return;
    }
    try {
      const session = await startCheckout({ businessId: createdId, priceId: selectedPrice.id });
      window.location.href = session.gatewayPageURL;
    } catch {
      // Business exists with an open invoice — send the owner to Billing to pay.
      navigate({ to: '/billing' });
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Add a new business"
        subtitle="Each business gets its own subscription and POS."
      />

      <div className="mb-8 flex justify-center">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="p-5 sm:p-8">
        <form onSubmit={onSubmit} className="space-y-6">
          {step === 0 && (
            <div className="space-y-5">
              {/* No placeholders — sample values read as prefilled data. Anything a
                  placeholder carried lives in `hint`, which stays visible while typing. */}
              <Input
                label="Business name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="POS subdomain"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.replace(/[^a-z0-9-]/g, '').toLowerCase())}
                hint={
                  db === 'managed'
                    ? `Your POS will be at ${posHostPreview}. Leave blank to derive it from the name.`
                    : 'Optional — lowercase letters, digits and dashes (min 3). Leave blank to derive from the name.'
                }
                error={subdomainValid ? undefined : 'Lowercase letters, digits and dashes only (min 3 characters).'}
              />

              {offeredModes.length > 0 && (
                <div>
                  <div className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Database</div>
                  {/* One mode available → state it. A lone selectable card reads as a
                      choice the user still has to make. */}
                  {offeredModes.length === 1 && offeredModes[0] === 'managed' && (
                    <p className="mb-2 text-xs text-zinc-600 dark:text-zinc-300">
                      This business gets its own dedicated database — nothing to choose here.
                    </p>
                  )}
                  <div className="space-y-3">
                    {canShared && (
                      <DbOption
                        selected={db === 'shared'}
                        onSelect={() => setDb('shared')}
                        title="Shared (recommended)"
                        badge="Default"
                        body={`We host it on the shared ${brand.name} platform. Fastest to start; nothing to manage.`}
                      />
                    )}
                    {canManaged && (
                      <DbOption
                        selected={db === 'managed'}
                        onSelect={() => setDb('managed')}
                        title="Isolated (your own database)"
                        body="A dedicated database on our server, just for this business — with its own POS address."
                      />
                    )}
                    {canSelfHosted && (
                      <DbOption
                        selected={db === 'self_hosted'}
                        onSelect={() => setDb('self_hosted')}
                        title="Your own MySQL server"
                        body="The POS runs on our servers, but the data stays in a MySQL database you host yourself. Give us a new, empty database and we set it up."
                      />
                    )}
                  </div>
                  {db === 'self_hosted' && <ByoDbFields value={byod} onChange={setByod} canTest />}
                </div>
              )}

              <Banner tone="info">
                The new business is created under your account — you stay the owner and can switch between
                businesses from the top bar.
              </Banner>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Billing interval</div>
                <div className="inline-flex rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden">
                  {(['month', 'year'] as const).map((iv) => (
                    <button
                      key={iv}
                      type="button"
                      onClick={() => setInterval(iv)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium transition',
                        interval === iv
                          ? 'bg-brand text-white'
                          : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                      )}
                    >
                      {iv === 'month' ? 'Monthly' : 'Yearly'}
                    </button>
                  ))}
                </div>
              </div>

              {sellable.length === 0 ? (
                plansLoading ? (
                  <div className="p-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Loading plans…
                  </div>
                ) : (
                  // An empty catalog is a real answer, not a slow one — the old
                  // spinner ran forever and read as a hang.
                  <Banner tone="warning" title="No plans available">
                    There are no published plans for {brand.name} right now. Please contact support and we'll
                    get one set up for you.
                  </Banner>
                )
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {sellable.map((p) => {
                    const price = priceFor(p.id, interval);
                    const active = planId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlanId(p.id)}
                        className={cn(
                          'text-left p-4 rounded-lg border transition',
                          active
                            ? 'border-brand ring-2 ring-brand/30 bg-brand/5'
                            : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</span>
                          {active && <Check className="w-4 h-4 text-brand" />}
                        </div>
                        <div className="mt-1.5 text-sm">
                          {price ? (
                            <>
                              <Money minor={price.amount} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
                              <span className="text-xs text-zinc-500 dark:text-zinc-400"> /{interval === 'month' ? 'mo' : 'yr'}</span>
                            </>
                          ) : (
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Not available {interval === 'month' ? 'monthly' : 'yearly'}</span>
                          )}
                        </div>
                        {p.tagline && (
                          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2">{p.tagline}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700">
                <ReviewRow icon={<Building2 className="w-4 h-4" />} label="Business" value={name.trim()} />
                <ReviewRow
                  icon={<Building2 className="w-4 h-4" />}
                  label="POS subdomain"
                  value={subdomain.trim() || 'derived from the name'}
                />
                <ReviewRow
                  icon={<Server className="w-4 h-4" />}
                  label="Database"
                  value={
                    db === 'managed' ? (
                      <>
                        Isolated — your own database
                        {subdomain.trim() && <span className="block text-xs text-zinc-500 dark:text-zinc-400 mono">{posHostPreview}</span>}
                      </>
                    ) : db === 'self_hosted' ? (
                      <>
                        Your own MySQL server
                        <span className="block text-xs text-zinc-500 dark:text-zinc-400 mono">
                          {byod.username.trim() || 'user'}@{byod.host.trim() || 'your-server'}:{byod.port || '3306'}/{byod.dbName.trim() || 'database'}
                        </span>
                      </>
                    ) : (
                      'Shared platform database'
                    )
                  }
                />
                <ReviewRow
                  icon={<CreditCard className="w-4 h-4" />}
                  label="Plan"
                  value={
                    <>
                      {selectedPkg?.name ?? '—'} · {interval === 'month' ? 'monthly' : 'yearly'} ·{' '}
                      {selectedPrice ? <Money minor={selectedPrice.amount} hideCents tone="neutral" className="font-medium" /> : '—'}
                    </>
                  }
                />
              </div>

              <Banner tone="info" title="What happens next">
                Your business is created immediately and you're taken to the secure {brand.name} payment page
                (SSLCommerz) to pay the first {interval === 'month' ? 'month' : 'year'}. If you don't complete the
                payment, the invoice stays open under <strong>Billing</strong> and you can pay any time.
              </Banner>

              {/* Immediately before "Create & pay" — the moment a charge is committed. */}
              <PolicyConsentCheckbox
                checked={acceptedPolicies}
                onChange={setAcceptedPolicies}
                id="new-business-consent"
              />

              {error && <Banner tone="error" title="Couldn’t create the business">{error}</Banner>}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            {step > 0 ? (
              <Button type="button" variant="ghost" iconLeft={<ArrowLeft className="w-4 h-4" />} onClick={() => setStep((s) => s - 1)} disabled={submitting}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" loading={submitting} disabled={!stepValid || submitting} iconRight={step < STEPS.length - 1 ? <ArrowRight className="w-4 h-4" /> : undefined}>
              {step < STEPS.length - 1 ? 'Continue' : 'Create & pay'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** Database choice card — same options and wording as the /signup "Database" step. */
function DbOption({
  selected,
  onSelect,
  title,
  badge,
  body,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  badge?: string;
  body: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'w-full text-left p-4 rounded-lg border transition',
        selected
          ? 'border-brand ring-2 ring-brand/30 bg-brand/5'
          : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
      )}
    >
      <div className="flex items-start gap-3">
        <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-zinc-950 dark:text-zinc-50">{title}</span>
            {badge && (
              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {badge}
              </span>
            )}
            {selected && <Check className="w-4 h-4 text-brand" />}
          </div>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">{body}</p>
        </div>
      </div>
    </button>
  );
}

function ReviewRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
        {icon}
      </span>
      <span className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-zinc-950 dark:text-zinc-50 min-w-0">{value}</span>
    </div>
  );
}
